import React from 'react';
import { isIOS, isAndroid, getHHSAppStoreUrl } from '../utils/platformDetect';

/**
 * HHS install nudge — shown wherever a feature is degraded by having no device data.
 *
 * WHY THIS EXISTS (#89, Vincent 2026-08-05: "remind them of that at every opportunity").
 * HealistHealthSync is the single highest-value setup step: no HHS → no wearable data → no Digital
 * Twin baselines → no anomaly detection → no objective progress tracking. A user who skips it gets
 * an app that looks like it is not working, and blames the product rather than the missing install.
 *
 * ⚠️ PLATFORM-CONDITIONAL, DELIBERATELY. `getHHSAppStoreUrl()` returns null on Android
 * (platformDetect.js:38 — "TODO: Add Google Play URL when Android HHS ships"), because the Android
 * build DOES NOT EXIST. Nagging an Android user toward an app they cannot install is worse than
 * saying nothing: it is a dead end that reads as a broken link, and it repeats at every surface
 * this component appears on. Android therefore gets an HONEST STATUS, never a call to action.
 *
 * Props:
 *   reason   — what is degraded right now, e.g. "This objective can't be tracked yet"
 *   compact  — inline one-liner (list rows) vs full card (empty states)
 *   onOpenTutorial — optional; deep-links to the `ios-health-sync` tutorial, which was promoted to
 *                    getting-started order 1 so it is the first thing in the tutorial list.
 */
export default function HHSInstallNudge({ reason, compact = false, onOpenTutorial }) {
  const url = getHHSAppStoreUrl();

  // Desktop browser: HHS is a phone app. Telling a desktop user to "install it" here is noise —
  // they cannot act on it from this device. Say where it has to happen instead.
  if (!isIOS && !isAndroid) {
    return (
      <div className={compact ? 'hhs-nudge hhs-nudge--compact' : 'hhs-nudge'}>
        <span className="hhs-nudge-icon">⌚</span>
        <div className="hhs-nudge-body">
          {reason && <strong>{reason}</strong>}
          <span>
            Your watch and health data come in through HealistHealthSync, a free app for
            iPhone and iPad. Install it on your phone to get started.
          </span>
          {onOpenTutorial && (
            <button className="btn btn-secondary btn-sm" onClick={onOpenTutorial}>
              How it works
            </button>
          )}
        </div>
      </div>
    );
  }

  // Android: HONEST STATUS, NO CALL TO ACTION. There is nothing to install.
  if (isAndroid) {
    return (
      <div className={compact ? 'hhs-nudge hhs-nudge--compact' : 'hhs-nudge'}>
        <span className="hhs-nudge-icon">ℹ️</span>
        <div className="hhs-nudge-body">
          {reason && <strong>{reason}</strong>}
          <span>
            HealistHealthSync for Android is coming soon. Until it’s available, wearable devices
            can’t connect to Healist on Android. Everything else works normally.
          </span>
        </div>
      </div>
    );
  }

  // iOS: the actionable case.
  return (
    <div className={compact ? 'hhs-nudge hhs-nudge--compact' : 'hhs-nudge'}>
      <span className="hhs-nudge-icon">⌚</span>
      <div className="hhs-nudge-body">
        {reason && <strong>{reason}</strong>}
        <span>
          Install <b>HealistHealthSync</b> from the App Store so Healist can see your watch and
          health data. It’s what lets us show your trends and track progress on your goals.
        </span>
        <div className="hhs-nudge-actions">
          {url && (
            <a className="btn btn-primary btn-sm" href={url} target="_blank" rel="noreferrer">
              Get it on the App Store
            </a>
          )}
          {onOpenTutorial && (
            <button className="btn btn-secondary btn-sm" onClick={onOpenTutorial}>
              Show me how
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
